import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Form, Input, Table } from 'tdesign-react';
import { round } from 'lodash';
import { useMemoizedFn } from 'ahooks';
import { connect } from 'react-redux';
import ShadowCard from '@/components/ShadowCard';
import SvgIcon from '@/components/SvgIcon';
import { isVoid, renderByteRate, renderDateTime, renderNum, renderNumRate } from '@/utils/common';
import ColTip from '@/components/Tip';
import { getBrokerList } from '@/services/brokerService';
import useTable from '@/hooks/useTable';
import { getCurrentCluster } from '@/pages/Layout/MenuLayout/CulsterAddon';
import Dashboard from '../Dashboard';
import Detail from './Detail';

const { FormItem } = Form;

function Body({ clusterName, currentCluster }) {
  const [visible, setVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState({});

  const isVm = currentCluster?.deployEnv === 'vm';

  const columns = [
    {
      colKey: 'host',
      sorter: true,
      title: '地址',
      fixed: 'left',
      cell: ({ row }) => <Detail detail={row} clusterName={clusterName} />,
      width: 200,
    },
    {
      colKey: 'port',
      title: '端口',
      sorter: true,
      width: 110,
      cell: ({ row }) => `${row.port}, ${row.metricsPort}`,
    },
    {
      colKey: 'version',
      title: '版本',
      sorter: true,
      width: 140,
    },
    {
      colKey: 'topicPartitionCount',
      title: (
        <ColTip
          content={
            <>
              <strong>分区个数</strong>
              <li>指该broker实际承载了多个Topic，按最小粒度，子分区粒度。</li>
            </>
          }
        >
          分区个数
        </ColTip>
      ),
      width: 140,
      sorter: true,
      cell: renderNum,
    },
    {
      colKey: 'bundleCount',
      title: (
        <ColTip
          content={
            <>
              <strong>Bundles</strong>
              <li>Pulsar集群中负载均衡最小粒度。</li>
            </>
          }
        >
          Bundles
        </ColTip>
      ),
      cell: renderNum,
      width: 120,
      sorter: true,
    },
    {
      colKey: 'bytesIn',
      title: '生产吞吐',
      cell: renderByteRate,
      width: 120,
      sorter: true,
    },
    {
      colKey: 'bytesOut',
      title: '消费吞吐',
      width: 120,
      cell: renderByteRate,
      sorter: true,
    },
    {
      colKey: 'rateIn',
      title: '生产速率',
      width: 120,
      cell: renderNumRate,
      sorter: true,
    },
    {
      colKey: 'rateOut',
      title: '消费速率',
      width: 120,
      cell: renderNumRate,
      sorter: true,
    },

    {
      colKey: 'connectCount',
      title: '连接数',
      width: 100,
      sorter: true,
      cell: renderNum,
    },
    isVm
      ? {
          colKey: 'overLoad',
          title: '负载',
          sorter: true,
          width: 100,
          cell: ({ row }) => `${round(row.overLoad, 2)}%`,
        }
      : null,
    {
      colKey: 'createTime',
      title: <ColTip content='第一次采集时间'>创建时间</ColTip>,
      sorter: true,
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'updateTime',
      sorter: true,
      title: <ColTip content='非实时，有分钟级延迟'>采集时间</ColTip>,
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Icon
          type='presentation-chart-line'
          size={18}
          onClick={() => {
            setVisible(true);
            setCurrentRow(row);
          }}
        />
      ),
      width: 60,
    },
  ];

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getBrokerList({ ...params, clusterName });
    setData(data.brokerMetricsList);
  });

  const { header, config, setData, doSearch } = useTable({
    search: (
      <FormItem name='host'>
        <Input placeholder='输入Broker地址' clearable />
      </FormItem>
    ),
    columns: columns.filter((item) => !isVoid(item)),
    onSearch: loadData,
  });

  useEffect(() => {
    if (clusterName) {
      doSearch();
    }
  }, [clusterName, doSearch]);

  const handleClose = () => {
    setVisible(false);
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <Dashboard
        clusterName={clusterName}
        detail={currentRow}
        visible={visible}
        handleClose={handleClose}
      />
    </Root>
  );
}

export default connect((state) => ({
  currentCluster: getCurrentCluster(state.cluster.allList),
}))(Body);

const Root = styled(ShadowCard)`
  flex: 1;
  margin-top: 12px;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
